/**
 * Unified API Types for WWP Dashboard
 * Based on backend WwpErrorResponse structure
 */

/**
 * Standardized error response from backend
 * Matches WWP.PathInsight.Dashboard.Models.WwpErrorResponse
 */
export interface WwpErrorResponse {
  status: number;
  code: string;
  longCode: string;
  message: string;
  errors?: WwpValidationError[];
}

/**
 * Validation error structure for field-level errors
 */
export interface WwpValidationError {
  key: string;
  values: string[];
}

/**
 * Type guard to check if a response is a WwpErrorResponse
 */
export function isWwpError(response: any): response is WwpErrorResponse {
  return Boolean(
    response &&
    typeof response === 'object' &&
    typeof response.status === 'number' &&
    typeof response.code === 'string' &&
    typeof response.longCode === 'string' &&
    typeof response.message === 'string'
  );
}

/**
 * Error categories based on backend error prefixes
 */
export enum ErrorCategory {
  ACCOUNT = 'ACC',
  AUTHENTICATION = 'AUTH',
  PATIENT = 'PTNT',
  HCP = 'HCP',
  INVITE = 'INVITE',
  GENERIC = 'GEN',
  PATIENT_COMPLIANCE = 'PCOMP',
  ACTIVE_CUSTOM_TEST = 'ACTEST',
  ACTIVE_TEST = 'ACTTEST',
  INTEGRATIONS = 'INTEG',
  SETTINGS = 'SETT',
}

/**
 * Custom error class for business logic errors
 */
export class BusinessError extends Error {
  constructor(
    message: string,
    public code: string,
    public longCode: string,
    public status: number
  ) {
    super(message);
    this.name = 'BusinessError';
  }

  getCategory(): ErrorCategory | null {
    const prefix = this.code.split('_')[0];
    return Object.values(ErrorCategory).includes(prefix as ErrorCategory)
      ? (prefix as ErrorCategory)
      : null;
  }
}

/**
 * Custom error class for validation errors
 */
export class ValidationError extends Error {
  constructor(
    message: string,
    public errors: WwpValidationError[]
  ) {
    super(message);
    this.name = 'ValidationError';
  }

  getFieldErrors(fieldName: string): string[] {
    const fieldError = this.errors.find(
      (e) => e.key.toLowerCase() === fieldName.toLowerCase()
    );
    return fieldError?.values || [];
  }

  getAllErrors(): Record<string, string[]> {
    return this.errors.reduce(
      (acc, error) => {
        acc[error.key] = error.values;
        return acc;
      },
      {} as Record<string, string[]>
    );
  }
}

/**
 * Network error class for connection issues
 */
export class NetworkError extends Error {
  constructor(message: string = 'Network error. Please check your connection.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Request options for API calls
 */
export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean; // Skip adding Authorization header
  skipErrorToast?: boolean; // Skip showing error toast in development
}

